import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { authErrorKey, confirmSignUp, resendConfirmationCode } from "@/auth/cognito/client";

import { AuthLayout } from "./AuthLayout";
import { Field, FormError, FormNotice, SubmitButton } from "./fields";

/**
 * Enter the 6-digit code Cognito emailed after sign-up. Until this succeeds the
 * account exists but can't sign in.
 *
 * <p>The address travels in the query string rather than router state so the
 * page survives a reload and can be reached from a cold start — someone who
 * closed the tab, or who tried to sign in before confirming (SignInPage
 * redirects that case here).</p>
 */
export function ConfirmSignUpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const emailParam = params.get("email") ?? "";

  const [email, setEmail] = useState(emailParam);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await confirmSignUp(email, code);
      navigate(`/signin?confirmed=1&email=${encodeURIComponent(email.trim())}`, { replace: true });
    } catch (err) {
      setError(t(authErrorKey((err as { code?: string }).code ?? "UnknownError")));
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await resendConfirmationCode(email);
      setNotice(t("auth.confirm.resent"));
    } catch (err) {
      setError(t(authErrorKey((err as { code?: string }).code ?? "UnknownError")));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={t("auth.confirm.title")}
      subtitle={t("auth.confirm.subtitle", { email: email || t("auth.confirm.yourEmail") })}
      footer={
        <Link to="/signin" className="font-medium text-teal-700 hover:text-teal-800">
          {t("auth.backToSignIn")}
        </Link>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <FormNotice>{notice}</FormNotice>
        {!emailParam && (
          <Field
            label={t("auth.fields.email")}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        )}
        <Field
          label={t("auth.fields.code")}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          required
        />
        <FormError>{error}</FormError>
        <SubmitButton busy={busy}>{t("auth.confirm.submit")}</SubmitButton>
      </form>
      <button
        type="button"
        onClick={() => void onResend()}
        disabled={busy || !email}
        className="mt-4 w-full text-center text-sm font-medium text-teal-700 transition hover:text-teal-800 disabled:opacity-50"
      >
        {t("auth.confirm.resend")}
      </button>
    </AuthLayout>
  );
}
